import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import commonStyle from '../../style.module.scss'
import { storeExtension } from '../../constants.ts'
import { IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import style from './Footer.module.scss'
import homeIcon from '../assets/icons/home.svg'
import basketGreyIcon from '../assets/icons/basketGrey.svg'
import basketWhiteIcon from '../assets/icons/basketWhite.svg'
import searchGreyIcon from '../assets/icons/searchGrey.svg'
import searchWhiteIcon from '../assets/icons/search.svg'
import facebookIcon from '../assets/icons/socialFacebook.svg'
import xIcon from '../assets/icons/socialX.svg'
import pinterestIcon from '../assets/icons/socialPinterest.svg'
import youtubeIcon from '../assets/icons/socialYoutube.svg'
import instagramIcon from '../assets/icons/socialInstagram.svg'
import visaIcon from '../assets/icons/paymentVisaSecure.svg'
import mastercardIcon from '../assets/icons/paymentMastercardIdCheck.svg'
import amexIcon from '../assets/icons/paymentAmexSafeKey.svg'

const COLUMNS = [
  {
    title: 'colHelp',
    links: [
      'linkMyAccount',
      'linkGroceryOrders',
      'linkHelpFaqs',
      'linkProductRecall',
      'linkPrivacyCentre',
      'linkPharmacy',
      'linkPhoto',
      'linkMagazine'
    ]
  },
  {
    title: 'colAbout',
    links: [
      'linkAccessibility',
      'linkSitemap',
      'linkPrivacyCookies',
      'linkCookieSettings',
      'linkGeneralTerms',
      'linkProductTerms',
      'linkCouponTerms',
      'linkRatingsPolicy',
      'linkPartnerDirectory'
    ]
  },
  {
    title: 'colWaysToSave',
    links: ['linkDeliverySaver', 'linkClubcard', 'linkClickCollect', 'linkClubcardPrices']
  },
  {
    title: 'colSupport',
    links: ['linkContactUs', 'linkStoreLocator']
  }
]

const SOCIAL = [
  { src: facebookIcon, key: 'socialFacebook' },
  { src: xIcon, key: 'socialX' },
  { src: pinterestIcon, key: 'socialPinterest' },
  { src: youtubeIcon, key: 'socialYoutube' },
  { src: instagramIcon, key: 'socialInstagram' }
]

const PAYMENTS = [
  { src: visaIcon, key: 'paymentVisa' },
  { src: mastercardIcon, key: 'paymentMastercard' },
  { src: amexIcon, key: 'paymentAmex' }
]

interface IFooterProps {
  schema: IEcommerceQuestionSchema
  totalPrice: number
  findability: boolean
  showCart: boolean
  onCartOpen: () => void
  onSearchClick: () => void
  showHint: () => void
  closeSimulation: () => void
}

const Footer = ({
  schema,
  totalPrice,
  findability,
  showCart,
  onCartOpen,
  onSearchClick,
  showHint,
  closeSimulation
}: IFooterProps) => {
  const { t, localeID, languageTag } = useSurveyLocale()
  const viewport = useViewport()

  if (viewport.mobile) {
    // The tab is narrow: up to 99.99 the total fits whole, from 100 the pence go and an ellipsis marks the cut.
    const basketLabel =
      totalPrice <= 0
        ? t('questions', 'ec', 'tesco', 'basket')
        : totalPrice < 100
          ? formatPriceWithLocale(totalPrice, schema.price_mask, languageTag, true, false)
          : formatPriceWithLocale(totalPrice, schema.price_mask, languageTag).replace('</ins>', '…</ins>')

    const basketTab = (
      <span className={`${style.tab} ${showCart ? style.tabActive : ''}`}>
        <span className={style.tabPill}>
          <img src={showCart ? basketWhiteIcon : basketGreyIcon} alt="" role="presentation" className={style.tabIcon} />
        </span>
        <span className={style.tabLabel} dangerouslySetInnerHTML={{ __html: basketLabel }} />
      </span>
    )

    return (
      <footer className={style.tabBar} data-testid="SW-EC-Footer">
        <div className={style.tabSlot}>
          <EmptyLink color="#00539f">
            <span className={style.tab}>
              <span className={style.tabPill}>
                <img src={homeIcon} alt="" role="presentation" className={style.tabIcon} />
              </span>
              <span className={style.tabLabel}>{t('questions', 'ec', 'tesco', 'home')}</span>
            </span>
          </EmptyLink>
        </div>
        <div className={style.tabSlot}>
          {findability ? (
            <EmptyLink color="#00539f">{basketTab}</EmptyLink>
          ) : (
            <button
              type="button"
              className={style.tabButton}
              onClick={onCartOpen}
              aria-label={t('questions', 'ec', 'tesco', 'basket')}
            >
              {basketTab}
            </button>
          )}
        </div>
        <div className={style.tabSlot}>
          <button type="button" className={style.tabButton} onClick={onSearchClick}>
            <span className={`${style.tab} ${showCart ? '' : style.tabActive}`}>
              <span className={style.tabPill}>
                <img
                  src={showCart ? searchGreyIcon : searchWhiteIcon}
                  alt=""
                  role="presentation"
                  className={style.tabIcon}
                />
              </span>
              <span className={style.tabLabel}>{t('questions', 'ec', 'tesco', 'search')}</span>
            </span>
          </button>
        </div>
        {[
          { key: 'hint', icon: 'icon-aytmfd-help', onClick: showHint },
          { key: 'exit', icon: 'icon-aytmfd-exit', onClick: closeSimulation }
        ].map(({ key, icon, onClick }) => (
          <div className={style.tabSlot} key={key}>
            <button type="button" className={style.tabButton} onClick={onClick}>
              <span className={style.tab}>
                <span className={style.tabPill}>
                  <span className={`${commonStyle.IconAytmPlace} ${style.helperIcon}`}>
                    <i className={icon} />
                  </span>
                </span>
                <span className={style.tabLabel}>{t('questions', 'ec', 'tesco', key)}</span>
              </span>
            </button>
          </div>
        ))}
      </footer>
    )
  }

  return (
    <footer data-testid="SW-EC-Footer">
      <div className={style.top}>
        <div className={style.inner}>
          <div className={style.columns}>
            {COLUMNS.map(col => (
              <div className={style.column} key={col.title}>
                <div className={style.columnTitle}>{t('questions', 'ec', 'tesco', col.title)}</div>
                {col.links.map(link => (
                  <EmptyLink key={link} color="#00539f">
                    <span className={style.columnLink}>{t('questions', 'ec', 'tesco', link)}</span>
                  </EmptyLink>
                ))}
              </div>
            ))}
          </div>
          <div className={style.social}>
            {SOCIAL.map(({ src, key }) => (
              <EmptyLink key={key} color="#00539f">
                <img src={src} alt={t('questions', 'ec', 'tesco', key)} className={style.socialIcon} />
              </EmptyLink>
            ))}
          </div>
        </div>
      </div>
      <div className={style.copyrightBar}>
        <div className={style.inner}>
          <span className={style.copyright}>
            {t<(domain: string) => string>('questions', 'ec', 'tesco', 'copyright')(storeExtension('tescow', localeID))}
          </span>
        </div>
      </div>
      <div className={style.payments}>
        <div className={style.inner}>
          {PAYMENTS.map(({ src, key }) => (
            <img key={key} src={src} alt={t('questions', 'ec', 'tesco', key)} className={style.paymentIcon} />
          ))}
        </div>
      </div>
    </footer>
  )
}

export default Footer
